import { HttpErrorResponse } from '@angular/common/http';

/**
 * Reads the several shapes a failed Gateway call arrives in, and says which one it was.
 *
 * The status code alone does not identify them. A Community or house failure arrives PascalCase as
 * `{ Error, Message }`; an unmapped orchestrator failure arrives as a 500 in that same shape; and a role
 * refusal is written by the authorization filter before the error middleware ever runs, camelCase, as
 * `{ error, data }` — so a 403 can be either an ownership refusal or a role refusal, told apart only by
 * the body. Three more cases come from this side of the wire rather than from the server: the HTTP layer
 * pipes an RxJS timeout that throws a TimeoutError rather than an HttpErrorResponse, the auth interceptor
 * synthesises a bodyless 401 when a silent refresh fails, and a large upload can be refused by the server
 * at the connection with no body at all.
 *
 * A pure function rather than a service, so the interceptor and any caller can read an error without
 * taking a dependency on a module.
 */

export type ApiErrorKind =
  | 'validation'
  | 'unauthenticated'
  | 'roleRefusal'
  | 'forbidden'
  | 'notFound'
  | 'conflict'
  | 'tooLarge'
  | 'timeout'
  | 'server'
  | 'offline'
  | 'unknown';

export interface ApiError {
  kind: ApiErrorKind;
  status: number;
  /** The server's own sentence, in English, or '' when there was no body. Safe to show. */
  message: string;
  /** The `Error` member. In dev and staging this is a whole exception dump, so it is for logging only. */
  detail: string;
  /** A key in en.json and sv.json for this kind. Always set. */
  translationKey: string;
  raw: unknown;
}

const TRANSLATION_KEYS: Record<ApiErrorKind, string> = {
  validation: 'knowledge.errors.validation',
  unauthenticated: 'knowledge.errors.unauthenticated',
  roleRefusal: 'knowledge.errors.role-refusal',
  forbidden: 'knowledge.errors.forbidden',
  notFound: 'knowledge.errors.not-found',
  conflict: 'knowledge.errors.conflict',
  tooLarge: 'knowledge.errors.too-large',
  timeout: 'knowledge.errors.timeout',
  server: 'knowledge.errors.server',
  offline: 'knowledge.errors.offline',
  unknown: 'generic.error',
};

/**
 * The two kinds whose server sentence carries information a fixed string cannot: which limit was exceeded,
 * and which job is already running. Everywhere else the sentence is a restatement of the status.
 */
const KINDS_WORTH_QUOTING: readonly ApiErrorKind[] = ['validation', 'conflict'];

const KIND_BY_STATUS: Record<number, ApiErrorKind> = {
  400: 'validation',
  401: 'unauthenticated',
  403: 'forbidden',
  404: 'notFound',
  409: 'conflict',
  413: 'tooLarge',
  500: 'server',
  504: 'timeout',
};

export function readApiError(error: unknown): ApiError {
  if (isTimeout(error)) {
    return build('timeout', 0, '', '', error);
  }

  if (!(error instanceof HttpErrorResponse)) {
    return build('unknown', 0, '', '', error);
  }

  if (error.status === 0) {
    return build('offline', 0, '', '', error);
  }

  const body: unknown = error.error;
  const kind = isRoleRefusal(error.status, body) ? 'roleRefusal' : (KIND_BY_STATUS[error.status] ?? 'unknown');

  return build(kind, error.status, readMessage(body), readDetail(body), error);
}

/**
 * What to hand SnackBarService.error(). It translates whatever it is given, and an unresolved key comes
 * back verbatim because no missing-translation handler is registered — which is what lets the two
 * informative kinds show the server's own sentence through the same call as every other kind.
 */
export function apiErrorSnackKey(apiError: ApiError): string {
  if (KINDS_WORTH_QUOTING.includes(apiError.kind) && apiError.message) {
    return apiError.message;
  }
  return apiError.translationKey;
}

function build(kind: ApiErrorKind, status: number, message: string, detail: string, raw: unknown): ApiError {
  return { kind, status, message, detail, translationKey: TRANSLATION_KEYS[kind], raw };
}

/** RxJS reports its own timeout by name; it is not an HttpErrorResponse and has no status. */
function isTimeout(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { name?: string }).name === 'TimeoutError';
}

/** The authorization filter's body is the only one with a lowercase `error` member beside a `data` member. */
function isRoleRefusal(status: number, body: unknown): boolean {
  if (status !== 403 || !isRecord(body)) {
    return false;
  }
  return typeof body['error'] === 'string' && 'data' in body;
}

function readMessage(body: unknown): string {
  if (typeof body === 'string') {
    return body;
  }
  if (!isRecord(body)) {
    return '';
  }
  const message = body['Message'] ?? body['error'] ?? body['Error'];
  return typeof message === 'string' ? message : '';
}

function readDetail(body: unknown): string {
  if (!isRecord(body)) {
    return '';
  }
  return typeof body['Error'] === 'string' ? body['Error'] : '';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
